<!-- Componet for printing multiple slides -->
<template>
	<div
		class="print-container relative h-full w-full overflow-y-scroll pt-14 pr-8 pl-6 print:mt-8 print:max-w-300 print:min-w-300 print:p-0!"
	>
		<!-- Overlay -->
		<div
			v-if="showOverlay"
			class="fixed inset-0 z-50 flex items-center justify-center bg-white/90"
		>
			<div class="text-center">
				<p class="text-2xl font-semibold text-slate-700">
					{{ t('paymentOptionsSlide.print.overlay.preparing') }}
				</p>
				<p class="mt-2 text-sm text-slate-500">
					{{ t('paymentOptionsSlide.print.overlay.pleaseWait') }}
				</p>
			</div>
		</div>

		<template v-if="ready">
			<!-- Memorial Guide -->
			<section
				data-area="print"
				:class="{ 'print:break-after-page': hasCart }"
			>
				<MemorialGuidePrint />
			</section>

			<!-- Review Slide -->
			<section
				v-if="hasCart"
				data-area="print"
				:class="{ 'print:break-after-page': hasEligiblePaymentPlan }"
			>
				<h1
					class="text-primary-700 text-center font-serif text-4xl font-semibold print:mt-4"
				>
					{{ t('paymentOptionsSlide.print.costEstimateA') }}
				</h1>
				<ReviewSlide hidePrintTitle />
			</section>

			<!-- Payment Options -->
			<section
				v-if="hasEligiblePaymentPlan"
				data-area="print"
				class="px-10"
			>
				<h1
					class="text-primary-700 text-center font-serif text-4xl font-semibold print:mt-4"
				>
					{{ t('paymentOptionsSlide.print.costEstimateB') }}
				</h1>
				<div class="flex justify-end px-5">
					<PrintDate />
				</div>
				<PlanPaymentOptions
					v-for="(plan, index) in plans"
					:key="plan.id + plan.encryptionStatus"
					:plan="plan"
					:index="index"
					showTerms
					class="mb-15"
				/>
			</section>
		</template>

		<div
			v-else
			class="p-10 text-center text-slate-500"
		>
			{{ t('paymentOptionsSlide.print.loadingSession') }}
		</div>
	</div>
</template>

<script setup lang="ts">
import PrintDate from '@/components/common/PrintDate.vue'
import MemorialGuidePrint from '@/components/memorialGuide/MemorialGuidePrint.vue'
import PlanPaymentOptions from '@/components/merchandise/PlanPaymentOptions.vue'
import ReviewSlide from '@/components/slides/ReviewSlide.vue'
import { usePresentationLocale } from '@/i18n'
import { useLazyRenderWaiter } from '@/mixins/lazyRenderWaiter'
import { useMerchandise } from '@/mixins/merchandise'
import { usePaymentOptions } from '@/mixins/paymentOptions'
import { useSessionStore } from '@/stores/session'
import { onMounted, onBeforeUnmount, nextTick, ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const { t } = usePresentationLocale()

const { getPlanPaymentOptions } = usePaymentOptions()
const route = useRoute()
const router = useRouter()
const sessionStore = useSessionStore()
const showOverlay = ref(true)
const ready = ref(false)

useLazyRenderWaiter(onPrintReady)

const plans = computed(() => sessionStore.activePlans)

const hasCart = computed(() =>
	sessionStore.activePlans?.some(
		(plan) => plan.cartItems && plan.cartItems.length > 0
	)
)

// Check if there are any plans with a valid scenario to use for the payment options section
const hasEligiblePaymentPlan = computed(
	() =>
		sessionStore.activePlans?.some((plan) => {
			const { scenarioOutput } = getPlanPaymentOptions(plan)
			return !!scenarioOutput.value?.isValid
		}) ?? false
)

// After printing navigate back to the original page
async function handleAfterPrint() {
	showOverlay.value = false
	const from =
		(route.query.from as string) || `/session/${route.params.sessionId}/present`
	router.push(from)
}

// Print the current page after the content has rendered
onMounted(async () => {
	if (!sessionStore.activeSession) {
		await sessionStore.loadSession(route.params.sessionId as string)
	}
	ready.value = true

	window.addEventListener('afterprint', handleAfterPrint)
})

async function onPrintReady() {
	showOverlay.value = false

	// wait for the overlay to hide before printing
	await nextTick()

	window.print()
}

// Clean up the event listeners
onBeforeUnmount(() => {
	window.removeEventListener('afterprint', handleAfterPrint)
})
</script>
